import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LuBell,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuLayoutDashboard,
  LuLogOut,
  LuSearch,
  LuSettings,
} from 'react-icons/lu';
import { adminResources } from '../config/resources';
import { api } from '../api';
import {
  adminNavigation,
  adminPortfolioNavigation,
  adminSettingsNavigation,
  adminStandaloneItems,
  buildModulePath,
  createPortfolioNavItem,
  getPageTitle,
  projectMatchesSlug,
} from '../config/navigation';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useAdminTheme } from '../context/AdminThemeContext';
import AdminThemeToggle from './AdminThemeToggle';

const resourceKeys = new Set(adminResources.map((item) => item.key));
const SIDEBAR_STORAGE_KEY = 'admin_sidebar_collapsed';

function getInitials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'RT';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function isPathActive(path, location) {
  return `${location.pathname}${location.search}` === path;
}

function Tooltip({ label, show }) {
  if (!show || !label) return null;
  return (
    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-md dark:bg-white dark:text-slate-900">
      {label}
    </span>
  );
}

const AdminLayout = () => {
  const { user, logout } = useAdminAuth();
  const { isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true',
  );
  const [openGroups, setOpenGroups] = useState(() => ({
    ...adminNavigation.reduce((acc, group) => {
      acc[group.key] = group.defaultOpen !== false;
      return acc;
    }, {}),
    [adminPortfolioNavigation.key]: adminPortfolioNavigation.defaultOpen !== false,
    [adminSettingsNavigation.key]: adminSettingsNavigation.defaultOpen !== false,
  }));
  const [hoveredKey, setHoveredKey] = useState(null);
  const [portfolioProjects, setPortfolioProjects] = useState([]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    let isMounted = true;

    const loadPortfolioProjects = async () => {
      try {
        const response = await api.list('/api/projects');
        if (!isMounted) return;
        const items = (response.data || [])
          .filter((project) => project.isActive !== false && !project.isFeatured)
          .slice(0, 3);
        setPortfolioProjects(items);
      } catch {
        if (isMounted) setPortfolioProjects([]);
      }
    };

    loadPortfolioProjects();
    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search]);

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const pageTitle = useMemo(() => {
    if (location.pathname === '/admin') return 'Dashboard';

    const params = new URLSearchParams(location.search);
    const slug = params.get('slug');
    if (location.pathname.includes('/projects') && slug && params.get('featured') !== '1') {
      const project = portfolioProjects.find((item) => projectMatchesSlug(item, slug));
      if (project?.title) return project.title;
    }

    return getPageTitle(location.pathname, location.search);
  }, [location.pathname, location.search, portfolioProjects]);

  const userInitials = getInitials(user?.fullName || 'RAADSO TECH Admin');

  const portfolioMenuItems = useMemo(() => {
    return portfolioProjects.map((project, index) => ({
      ...createPortfolioNavItem(project),
      navKey: `portfolio-${project.id || project.slug || index}-${index}`,
    }));
  }, [portfolioProjects]);

  const allMenuItems = useMemo(() => {
    const grouped = adminNavigation.flatMap((group) =>
      group.items
        .filter((item) => resourceKeys.has(item.key))
        .map((item, index) => ({
          ...item,
          navKey: `${group.key}-${item.label}-${index}`,
        })),
    );

    const standalone = adminStandaloneItems
      .filter((item) => resourceKeys.has(item.key))
      .map((item, index) => ({
        ...item,
        navKey: `standalone-${item.label}-${index}`,
      }));

    return [...grouped, ...standalone, ...portfolioMenuItems];
  }, [portfolioMenuItems]);

  const settingsMenuItems = useMemo(
    () =>
      adminSettingsNavigation.items
        .filter((item) => item.key === 'profile-settings' || resourceKeys.has(item.key))
        .map((item, index) => ({
          ...item,
          navKey: `settings-${item.label}-${index}`,
        })),
    [],
  );

  const expandedNavItemClass = (isActive) =>
    [
      'group flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-all duration-200',
      isActive
        ? 'bg-slate-100 text-slate-900 dark:bg-white/10 dark:text-white'
        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white',
    ].join(' ');

  const expandedIconClass = (isActive) =>
    [
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
      isActive
        ? 'bg-primary/20 text-slate-900 dark:bg-primary/25 dark:text-primary'
        : 'text-slate-500 group-hover:text-slate-800 dark:text-gray-400 dark:group-hover:text-white',
    ].join(' ');

  const collapsedIconClass = (isActive) =>
    [
      'group relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
      isActive
        ? 'bg-white text-slate-900 shadow-sm dark:bg-white/10 dark:text-white'
        : 'text-slate-500 hover:bg-white/70 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white',
    ].join(' ');

  const renderExpandedNavItem = (item, groupKey, index) => {
    const Icon = item.icon;
    const path = buildModulePath(item);
    const navKey = `${groupKey}-${item.label}-${index}`;

    return (
      <NavLink
        key={navKey}
        to={path}
        className={({ isActive }) => expandedNavItemClass(isActive)}
        isActive={(_, navLocation) => isPathActive(path, navLocation)}
      >
        {({ isActive }) => (
          <>
            <span className={expandedIconClass(isActive)}>
              <Icon size={18} strokeWidth={1.75} />
            </span>
            <span className="truncate text-sm font-medium">{item.label}</span>
          </>
        )}
      </NavLink>
    );
  };

  const renderCollapsedIconLink = (to, label, icon, navKey, end = false) => {
    const Icon = icon;
    const active = end
      ? location.pathname === '/admin'
      : isPathActive(to, location);

    return (
      <NavLink
        key={navKey}
        to={to}
        end={end}
        className={collapsedIconClass(active)}
        isActive={end ? undefined : (_, navLocation) => isPathActive(to, navLocation)}
        onMouseEnter={() => setHoveredKey(navKey)}
        onMouseLeave={() => setHoveredKey(null)}
      >
        <Icon size={20} strokeWidth={1.75} />
        <Tooltip label={label} show={hoveredKey === navKey} />
      </NavLink>
    );
  };

  const renderCollapsedAction = (label, icon, navKey, onClick) => {
    const Icon = icon;
    return (
      <button
        key={navKey}
        type="button"
        onClick={onClick}
        className={collapsedIconClass(false)}
        onMouseEnter={() => setHoveredKey(navKey)}
        onMouseLeave={() => setHoveredKey(null)}
      >
        <Icon size={20} strokeWidth={1.75} />
        <Tooltip label={label} show={hoveredKey === navKey} />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#f4f5f8] text-slate-900 transition-colors dark:bg-[#050608] dark:text-white">
      <div className="mx-auto flex min-h-screen max-w-[1760px] gap-4 p-3 md:p-4">
        <aside
          className={[
            'relative sticky top-3 flex h-[calc(100vh-1.5rem)] shrink-0 flex-col overflow-hidden rounded-[24px] border shadow-[0_12px_40px_rgba(15,23,42,0.06)] transition-all duration-300 md:top-4 md:h-[calc(100vh-2rem)]',
            collapsed
              ? 'w-[72px] border-slate-200/60 bg-[#f3f4f7] dark:border-white/10 dark:bg-[#12151c]'
              : 'w-[280px] border-slate-200/80 bg-white dark:border-white/10 dark:bg-[#0f1117] dark:shadow-[0_12px_40px_rgba(0,0,0,0.35)]',
          ].join(' ')}
        >
          {collapsed ? (
            <>
              <div className="flex items-center justify-center border-b border-slate-200/70 px-2 py-3 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  aria-label="Expand sidebar"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  <LuChevronRight size={18} strokeWidth={2} />
                </button>
              </div>

              <nav className="flex flex-1 flex-col overflow-y-auto px-2 py-3">
                <div className="flex flex-col items-center gap-1">
                  {renderCollapsedIconLink('/admin', 'Dashboard', LuLayoutDashboard, 'dashboard', true)}

                  {allMenuItems.map((item) => {
                    const Icon = item.icon;
                    const path = buildModulePath(item);
                    const active = isPathActive(path, location);

                    return (
                      <NavLink
                        key={item.navKey}
                        to={path}
                        className={collapsedIconClass(active)}
                        isActive={(_, navLocation) => isPathActive(path, navLocation)}
                        onMouseEnter={() => setHoveredKey(item.navKey)}
                        onMouseLeave={() => setHoveredKey(null)}
                      >
                        <Icon size={20} strokeWidth={1.75} />
                        <Tooltip label={item.label} show={hoveredKey === item.navKey} />
                      </NavLink>
                    );
                  })}
                </div>

                <div className="mt-auto flex flex-col items-center gap-1 border-t border-slate-200/70 pt-3 dark:border-white/10">
                  {settingsMenuItems.map((item) => {
                    const Icon = item.icon;
                    const path = buildModulePath(item);
                    const active = isPathActive(path, location);

                    return (
                      <NavLink
                        key={item.navKey}
                        to={path}
                        className={collapsedIconClass(active)}
                        isActive={(_, navLocation) => isPathActive(path, navLocation)}
                        onMouseEnter={() => setHoveredKey(item.navKey)}
                        onMouseLeave={() => setHoveredKey(null)}
                      >
                        <Icon size={20} strokeWidth={1.75} />
                        <Tooltip label={item.label} show={hoveredKey === item.navKey} />
                      </NavLink>
                    );
                  })}
                  {renderCollapsedAction('Log out', LuLogOut, 'logout', handleLogout)}
                </div>
              </nav>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
                className="absolute -right-3 top-5 z-30 flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-md transition-colors hover:text-slate-900 dark:border-white/15 dark:bg-[#171a22] dark:text-gray-300 dark:hover:text-white"
              >
                <LuChevronLeft size={16} strokeWidth={2} />
              </button>

              <div className="border-b border-slate-100 pb-4 pl-3 pr-4 pt-3 dark:border-white/10">
                <Link to="/admin" className="inline-flex items-center">
                  <img
                    src={isDark ? '/img/logo-white.png' : '/img/logo-black.png'}
                    alt="RAADSO TECH"
                    className="h-10 w-auto max-w-[200px] object-contain object-left"
                  />
                </Link>
              </div>

              <nav className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-3 py-4">
                <div>
                  <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-gray-500">
                    My Panel
                  </p>
                  <NavLink to="/admin" end className={({ isActive }) => expandedNavItemClass(isActive)}>
                    {({ isActive }) => (
                      <>
                        <span className={expandedIconClass(isActive)}>
                          <LuLayoutDashboard size={18} strokeWidth={1.75} />
                        </span>
                        <span className="text-sm font-semibold">Dashboard</span>
                      </>
                    )}
                  </NavLink>
                </div>

                {adminNavigation.map((group) => {
                  const GroupIcon = group.icon;
                  const isOpen = openGroups[group.key];
                  const visibleItems = group.items.filter((item) => resourceKeys.has(item.key));

                  return (
                    <div key={group.key}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        className="mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 transition-colors hover:text-slate-700 dark:text-gray-500 dark:hover:text-gray-300"
                      >
                        <span className="inline-flex items-center gap-2">
                          <GroupIcon size={13} strokeWidth={2} />
                          {group.title}
                        </span>
                        {isOpen ? <LuChevronDown size={14} /> : <LuChevronRight size={14} />}
                      </button>

                      {isOpen ? (
                        <div className="space-y-1 border-l border-slate-100 pl-3 dark:border-white/10">
                          {visibleItems.map((item, index) => renderExpandedNavItem(item, group.key, index))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {(() => {
                  const PortfolioIcon = adminPortfolioNavigation.icon;
                  return (
                <div>
                  <button
                    type="button"
                    onClick={() => toggleGroup(adminPortfolioNavigation.key)}
                    className="mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 transition-colors hover:text-slate-700 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <span className="inline-flex items-center gap-2">
                      <PortfolioIcon size={13} strokeWidth={2} />
                      {adminPortfolioNavigation.title}
                    </span>
                    {openGroups[adminPortfolioNavigation.key] ? (
                      <LuChevronDown size={14} />
                    ) : (
                      <LuChevronRight size={14} />
                    )}
                  </button>

                  {openGroups[adminPortfolioNavigation.key] ? (
                    <div className="space-y-1 border-l border-slate-100 pl-3 dark:border-white/10">
                      {portfolioMenuItems.map((item, index) =>
                        renderExpandedNavItem(item, adminPortfolioNavigation.key, index),
                      )}
                    </div>
                  ) : null}
                </div>
                  );
                })()}

                <div className="space-y-1 border-t border-slate-100 pt-2 dark:border-white/10">
                  {adminStandaloneItems
                    .filter((item) => resourceKeys.has(item.key))
                    .map((item, index) => renderExpandedNavItem(item, 'standalone', index))}
                </div>
              </nav>

              <div className="space-y-1 border-t border-slate-100 px-3 py-4 dark:border-white/10">
                <div>
                  <button
                    type="button"
                    onClick={() => toggleGroup(adminSettingsNavigation.key)}
                    className="mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 transition-colors hover:text-slate-700 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <span className="inline-flex items-center gap-2">
                      <LuSettings size={13} strokeWidth={2} />
                      {adminSettingsNavigation.title}
                    </span>
                    {openGroups[adminSettingsNavigation.key] ? (
                      <LuChevronDown size={14} />
                    ) : (
                      <LuChevronRight size={14} />
                    )}
                  </button>

                  {openGroups[adminSettingsNavigation.key] ? (
                    <div className="space-y-1 border-l border-slate-100 pl-3 dark:border-white/10">
                      {settingsMenuItems.map((item, index) =>
                        renderExpandedNavItem(item, adminSettingsNavigation.key, index),
                      )}
                    </div>
                  ) : null}
                </div>

                <button
                  onClick={handleLogout}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition-colors hover:border-primary hover:text-black dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:border-primary dark:hover:text-primary"
                >
                  <LuLogOut size={14} strokeWidth={1.75} />
                  Logout
                </button>
              </div>
            </>
          )}
        </aside>

        <main className="min-h-[calc(100vh-1.5rem)] flex-1 rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_rgba(0,0,0,0.04)] dark:border-white/10 dark:bg-[#0a0d14] dark:shadow-[0_20px_50px_rgba(0,0,0,0.35)] md:min-h-[calc(100vh-2rem)] md:p-6">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-white/10 dark:bg-[#0d1220]">
            <div className="flex w-full max-w-md items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-slate-600 shadow-sm dark:border-white/10 dark:bg-[#0a1426] dark:text-gray-300">
              <LuSearch size={16} strokeWidth={1.75} className="text-primary" />
              <input
                placeholder="Search anything..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400 dark:placeholder:text-gray-500"
              />
            </div>

            <div className="ml-auto flex items-center gap-3">
              <AdminThemeToggle />
              <button className="rounded-2xl border border-slate-200 bg-white p-2.5 text-slate-600 shadow-sm transition-colors hover:text-primary dark:border-white/15 dark:bg-[#0a1426] dark:text-gray-200">
                <LuBell size={16} strokeWidth={1.75} />
              </button>
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-[#0a1426]">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user?.fullName || 'Admin'}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-extrabold text-black">
                    {userInitials}
                  </div>
                )}
                <div className="leading-tight">
                  <p className="text-xs font-bold text-slate-900 dark:text-white">{user?.fullName || 'RAADSO TECH Admin'}</p>
                  <p className="text-[11px] text-slate-500 dark:text-gray-400">Super Admin</p>
                </div>
              </div>
            </div>
          </div>

          {location.pathname === '/admin' ? null : (
            <div className="mb-4 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-xs text-slate-600 dark:border-primary/20 dark:bg-primary/10 dark:text-gray-300">
              <span className="font-bold text-slate-900 dark:text-white">{pageTitle}</span>
            </div>
          )}

          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
